/**
 * BlogCard - Article card component for blog listings
 * 
 * Based on RemUp design (gold standard)
 * Premium look with image, meta, title, excerpt, and author
 * 
 * Uses React.memo for performance optimization in lists
 */

import { useState, memo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText } from 'lucide-react';
import type { Article } from '@konstellation/shared';
import { getAuthorInitials } from '@konstellation/shared';
import { cn } from '../utils';

interface BlogCardProps {
  article: Article;
  /** Index for alternating layout (odd = reversed) */
  index?: number;
  /** Custom author subtitle */
  authorSubtitle?: string;
  /** Base path for blog links (default: /blog) */
  basePath?: string;
  /** Add trailing slash to links */
  trailingSlash?: boolean;
  /**
   * Couleur du petit texte d'accent (surtitre, « Lire l'article »). Défaut
   * `text-accent`. remup.fr passe son orange BRÛLÉ : son orange vif ne fait que
   * 2,6:1 en petit texte sur la crème (charte 2026).
   */
  classeAccentTexte?: string;
  /**
   * Attribut `sizes` de l'image. Présent, l'image est servie en `<picture>` avec les
   * variantes WebP `<image>-400w.jpg`, `-800w.jpg`, `-1200w.jpg` posées à côté de
   * l'original (convention du générateur d'articles). Absent : l'image seule, comme avant.
   * Seul remup.fr le passe — ses variantes sont verrouillées par un test (KST-2708).
   */
  sizesImage?: string;
  /** Image au-dessus de la ligne de flottaison : chargée tout de suite, pas en différé. */
  prioritaire?: boolean;
  /** Custom class names */
  className?: string;
}

/** `srcset` des variantes d'une image `….jpg[?v=N]`, ou `undefined` si elle n'a pas cette forme. */
export function srcSetVariantes(src: string): string | undefined {
  const m = src.match(/^(.+)\.jpg(\?.*)?$/);
  if (!m) return undefined;
  const [, base, requete = ''] = m;
  return [400, 800, 1200].map((l) => `${base}-${l}w.jpg${requete} ${l}w`).join(', ');
}

/** L'image d'une carte : `<picture>` WebP quand on lui donne `sizes`, `<img>` seule sinon. */
function ImageCarte({
  src,
  alt,
  sizes,
  prioritaire,
  onError,
}: {
  src: string;
  alt: string;
  sizes?: string;
  prioritaire?: boolean;
  onError: () => void;
}) {
  const img = (
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      loading={prioritaire ? 'eager' : 'lazy'}
      onError={onError}
    />
  );
  const srcSet = sizes ? srcSetVariantes(src) : undefined;
  if (!srcSet) return img;
  return (
    <picture>
      <source srcSet={srcSet} sizes={sizes} type="image/webp" />
      {img}
    </picture>
  );
}

/**
 * Placeholder component for missing images
 */
function ImagePlaceholder({ category }: { category?: string }) {
  return (
    <div className="w-full h-full bg-gradient-to-br from-primary/10 via-accent/5 to-muted flex items-center justify-center">
      <div className="text-center">
        <FileText className="w-12 h-12 text-primary/30 mx-auto mb-2" />
        {category && (
          <span className="text-xs text-muted-foreground/60 uppercase tracking-wide">
            {category}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Premium blog card with alternating layout (image left/right)
 * Matches RemUp's design system
 * Memoized for performance in lists
 */
export const BlogCard = memo(function BlogCard({
  article,
  index = 0,
  authorSubtitle = "Expert",
  basePath = "/blog",
  trailingSlash = true,
  classeAccentTexte = 'text-accent',
  sizesImage,
  prioritaire,
  className,
}: BlogCardProps) {
  const [imageError, setImageError] = useState(false);
  const isReversed = index % 2 === 1;
  const linkPath = `${basePath}/${article.slug}${trailingSlash ? '/' : ''}`;
  
  const showPlaceholder = !article.image || imageError;
  
  return (
    <Link
      to={linkPath}
      className={cn("group block", className)}
    >
      <article className={cn(
        "grid md:grid-cols-2 gap-8 md:gap-12 items-center",
        isReversed && "md:flex-row-reverse"
      )}>
        {/* Image */}
        <div className={cn(
          "aspect-[16/10] overflow-hidden rounded-2xl md:rounded-3xl bg-muted",
          isReversed && "md:order-2"
        )}>
          {showPlaceholder ? (
            <ImagePlaceholder category={article.category} />
          ) : (
            <ImageCarte
              src={article.image!}
              alt={article.title}
              sizes={sizesImage}
              prioritaire={prioritaire}
              onError={() => setImageError(true)}
            />
          )}
        </div>

        {/* Content */}
        <div className={cn(
          "space-y-4",
          isReversed && "md:order-1"
        )}>
          {/* Meta: Date + Read time */}
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <span>
              {new Date(article.date).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}
            </span>
            <span>•</span>
            <span>{article.readTime} de lecture</span>
          </div>

          {/* Title */}
          <h2 className="font-display text-2xl md:text-3xl font-medium text-foreground group-hover:text-accent transition-colors leading-snug">
            {article.title}
          </h2>

          {/* Excerpt */}
          <p className="text-muted-foreground line-clamp-2 md:line-clamp-3 leading-relaxed">
            {article.excerpt}
          </p>

          {/* Author */}
          <div className="flex items-center gap-3 pt-2">
            <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">
              {getAuthorInitials(article.author)}
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">{article.author}</p>
              <p className="text-xs text-muted-foreground">{authorSubtitle}</p>
            </div>
          </div>

          {/* Read more link */}
          <div className={cn("inline-flex items-center gap-2 font-medium group-hover:gap-3 transition-all pt-2", classeAccentTexte)}>
            Lire l'article <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </article>
    </Link>
  );
});

/**
 * Compact blog card for grid layouts
 * Memoized for performance in lists
 */
export const BlogCardCompact = memo(function BlogCardCompact({
  article,
  basePath = "/blog",
  trailingSlash = true,
  libelleCategorie,
  classeAccentTexte = 'text-primary',
  sizesImage,
  prioritaire,
  className,
}: Omit<BlogCardProps, 'index' | 'authorSubtitle'> & {
  /** Libellé lisible de la catégorie (sinon l'identifiant brut, `redevance-de-marque`). */
  libelleCategorie?: string;
}) {
  const [imageError, setImageError] = useState(false);
  const linkPath = `${basePath}/${article.slug}${trailingSlash ? '/' : ''}`;
  
  const showPlaceholder = !article.image || imageError;
  
  return (
    <Link
      to={linkPath}
      className={cn(
        "group flex flex-col bg-card rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-border/50",
        className
      )}
    >
      {/* Image */}
      <div className="aspect-video overflow-hidden">
        {showPlaceholder ? (
          <ImagePlaceholder category={article.category} />
        ) : (
          <ImageCarte
            src={article.image!}
            alt={article.title}
            sizes={sizesImage}
            prioritaire={prioritaire}
            onError={() => setImageError(true)}
          />
        )}
      </div>
      
      {/* Content */}
      <div className="flex flex-1 flex-col p-6">
        <span className={cn("text-xs font-semibold uppercase tracking-wide", classeAccentTexte)}>
          {libelleCategorie ?? article.category}
        </span>
        <h2 className="font-display text-lg font-semibold text-foreground mt-2 mb-3 group-hover:text-primary transition-colors line-clamp-2">
          {article.title}
        </h2>
        <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
          {article.excerpt}
        </p>
        <div className="mt-auto flex items-center gap-3 text-xs text-muted-foreground mb-3">
          <span>
            {new Date(article.date).toLocaleDateString('fr-FR', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            })}
          </span>
          <span>•</span>
          <span>{article.readTime}</span>
        </div>
        <span className={cn("inline-flex items-center gap-1 text-sm font-medium group-hover:gap-2 transition-all", classeAccentTexte)}>
          Lire l'article <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </Link>
  );
});
